import { useLocation } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { VideoRoom } from "protogen/video_service_pb";
import { AccountStub, FamilyStub } from "protogen/common_pb";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { LocalUserChoices } from "@livekit/components-react";
import TwoPanelGrid, { Panel } from "components/details-page/TwoPanelGrid";
import useIsMobile from "components/hooks/useIsMobile";
import VideoCallDetailsPanel from "components/details-page/VideoCallDetailsPanel";
import VideoCallActivityPanel from "components/details-page/VideoCallsActivityPanel";
import Breadcrumbs from "components/common/Breadcrumbs";
import CopyButton from "components/common/CopyButton";

const mainHeader = (family: FamilyStub | undefined) => (
  <Box
    display={"flex"}
    flexDirection={"row"}
    justifyContent={"space-between"}
    alignItems={"center"}
  >
    <Box>
      {family?.ref && (
        <Breadcrumbs
          breadcrumbs={[
            {
              name: family.name,
              link: `/families/${encodeURIComponent(family.ref)}`,
            },
          ]}
        />
      )}
      <Typography
        variant="h1Serif"
        sx={{
          margin: "4px 0",
        }}
      >
        Video call
      </Typography>
    </Box>
    <Box>
      <CopyButton
        value={window.location.href}
        variant="text"
        label="Copy invite link"
      />
    </Box>
  </Box>
);

const transcriptHeader = (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 0",
      gap: "12px",
      borderBottom: "1px solid #975d331a",
    }}
  >
    <Typography variant="h4">Transcript</Typography>
  </Box>
);

const ToggleButton = (isCollapsed: boolean, onToggle: () => void) => (
  <Button
    onClick={onToggle}
    variant="outlined"
    size="small"
    startIcon={isCollapsed ? <ChevronLeft /> : <ChevronRight />}
    sx={{ whiteSpace: "nowrap", marginLeft: "8px" }}
  >
    {isCollapsed ? "Show transcript" : "Hide transcript"}
  </Button>
);

export default ({
  videoRoom,
  participants,
  handleCallEnd,
  note,
  token,
  family,
  isCallActive,
  onReJoin,
  suggestions,
  onVideoSetupJoin,
  wasCreatingState,
  userChoices,
}: {
  videoRoom: VideoRoom | null;
  participants: AccountStub[];
  handleCallEnd: () => void;
  note: Note | null;
  token: string;
  family: FamilyStub | undefined;
  isCallActive: boolean;
  onReJoin: () => void;
  suggestions: Suggestions | null;
  onVideoSetupJoin?: (choices: LocalUserChoices) => void;
  wasCreatingState?: boolean;
  userChoices?: LocalUserChoices | null;
}) => {
  const isMobile = useIsMobile();
  const { hash } = useLocation();
  const defaultPanel = hash.match(/#details/) ? Panel.Left : Panel.Right;
  return (
    <>
      <TwoPanelGrid
        defaultPanel={defaultPanel}
        showHeaderAction={!isMobile}
        leftPanel={{
          title: mainHeader(family),
          switchContent: "Call details",
          content: (
            <VideoCallDetailsPanel
              handleCallEnd={handleCallEnd}
              note={note}
              token={token}
              isCallActive={isCallActive}
              onReJoin={onReJoin}
              videoRoom={videoRoom}
              suggestions={suggestions}
              familyRef={family?.ref || ""}
              onVideoSetupJoin={onVideoSetupJoin}
              wasCreatingState={wasCreatingState}
              userChoices={userChoices}
            />
          ),
          sx: { overflow: "hidden" },
        }}
        rightPanel={{
          title: isMobile ? mainHeader(family) : transcriptHeader,
          switchContent: "Transcript",
          content: (
            <VideoCallActivityPanel
              videoRoom={videoRoom}
              participants={participants}
            />
          ),
          collapsible: true,
          defaultCollapsed: true,
          customToggleButton: ToggleButton,
        }}
      />
    </>
  );
};
